/**
 * Centralized configuration for onboarding action styling.
 *
 * Single source of truth for icon, color, and time estimate mappings
 * used by OnboardingActionCard (proposals center) and ActionRow (dashboard).
 */

import { Lock01, Mail01, Rocket02, Target04, Users01 } from '@untitledui/icons';
import type { FC, SVGProps } from 'react';

import { PipelineIcon } from '@/lib/crm/utils/icons/CustomIcons';
import type { ProposalListItem } from '@/lib/proposals/core/types';

/**
 * Configuration for onboarding action styling.
 */
export interface OnboardingConfig {
  icon: FC<SVGProps<SVGSVGElement>>;
  color: 'warning' | 'success' | 'brand' | 'gray';
  timeEstimate: string;
}

/**
 * Source ID prefix/exact match configuration.
 * Keys can be exact matches (e.g., 'customize_pipeline') or prefixes (e.g., 'onboarding_').
 */
const SOURCE_ID_CONFIG: Record<string, OnboardingConfig> = {
  // Deal-pipeline setup wears the canonical pipeline icon.
  deal_pipeline_template: {
    icon: PipelineIcon,
    color: 'brand',
    timeEstimate: '2 mins',
  },
  welcome_import_: { icon: Mail01, color: 'warning', timeEstimate: '2 mins' },
  onboarding_: { icon: Users01, color: 'success', timeEstimate: '2 mins' },
  review_permissions_: { icon: Lock01, color: 'brand', timeEstimate: '2 mins' },
  customize_pipeline: {
    icon: Target04,
    color: 'brand',
    timeEstimate: '5 mins',
  },
  import_contacts: { icon: Users01, color: 'warning', timeEstimate: '5 mins' },
};

/**
 * Source type name configuration (for system-generated actions like imports).
 */
const SOURCE_TYPE_CONFIG: Record<string, OnboardingConfig> = {
  ahoy_gmail_import_job: {
    icon: Mail01,
    color: 'warning',
    timeEstimate: '1 min',
  },
};

const DEFAULT_CONFIG: OnboardingConfig = {
  icon: Rocket02,
  color: 'gray',
  timeEstimate: '2 mins',
};

/**
 * Prefixes to check in order for source_id matching.
 * Order matters: more specific prefixes should come first.
 */
const PREFIX_MATCH_ORDER = [
  'welcome_import_',
  'review_permissions_',
  'onboarding_',
] as const;

/**
 * Get onboarding configuration for an action.
 *
 * Matching strategy (in priority order):
 * 1. source_type_name exact match (for system-generated actions)
 * 2. source_id exact match
 * 3. source_id prefix match
 * 4. Fallback default (Rocket02 icon, gray, 2 mins)
 *
 * @example
 * // Gmail import action
 * getOnboardingConfig({ source_type_name: 'ahoy_gmail_import_job', ... })
 * // → { icon: Mail01, color: 'warning', timeEstimate: '1 min' }
 *
 * @example
 * // Onboarding team action
 * getOnboardingConfig({ source_id: 'onboarding_team', ... })
 * // → { icon: Users01, color: 'success', timeEstimate: '2 mins' }
 */
export function getOnboardingConfig(
  action: ProposalListItem
): OnboardingConfig {
  const { source_type_name, source_id } = action;

  // Tier 1: Source type match (system-generated actions)
  if (SOURCE_TYPE_CONFIG[source_type_name]) {
    return SOURCE_TYPE_CONFIG[source_type_name];
  }

  // Tier 2: Exact source_id match
  if (SOURCE_ID_CONFIG[source_id]) {
    return SOURCE_ID_CONFIG[source_id];
  }

  // Tier 3: Prefix matches
  for (const prefix of PREFIX_MATCH_ORDER) {
    if (source_id.startsWith(prefix)) {
      return SOURCE_ID_CONFIG[prefix];
    }
  }

  // Tier 4: Fallback
  return DEFAULT_CONFIG;
}

/**
 * The action-less onboarding "Review your import" welcome proposal. It is created
 * by `apps/onboarding/tasks/welcome_import.py` as a UI placeholder with `actions=[]`,
 * so the backend derives no `filter_tags` for it (filter tags come only from action
 * handlers). It is therefore identified by its source identity, not by a tag.
 */
export function isWelcomeProposal(proposal: {
  source_type_name?: string | null;
  source_id?: string | null;
}): boolean {
  return (
    proposal.source_type_name === 'ahoy_onboarding' &&
    (proposal.source_id?.startsWith('welcome_import_') ?? false)
  );
}

/**
 * Single source of truth for "is this proposal an onboarding proposal" across the
 * proposals UI. True when the backend tagged it `onboarding` (the normal case, where
 * the proposal carries an OnboardingAction) OR when it is the action-less welcome
 * placeholder, which can never carry that tag. Use this everywhere onboarding
 * membership is decided — grouping, priority-group exclusion, mode detection — so the
 * welcome proposal is treated consistently without a backend tag.
 */
export function isOnboardingProposal(proposal: {
  source_type_name?: string | null;
  source_id?: string | null;
  filter_tags?: readonly string[];
}): boolean {
  return (
    (proposal.filter_tags?.includes('onboarding') ?? false) ||
    isWelcomeProposal(proposal)
  );
}

/**
 * Check if a proposal is an onboarding proposal. Thin alias over
 * {@link isOnboardingProposal} (prefer that name in new code); delegates so the
 * action-less welcome placeholder is included.
 */
export function isOnboardingAction(action: ProposalListItem): boolean {
  return isOnboardingProposal(action);
}
